// Auth + agency context. `user` is the signed-in identity;
// `activeAgency` is the currently-selected agency (null when the user
// has none yet — create or wait-for-invite depending on SIGNUP_MODE). Switching agencies
// re-fetches downstream API state.
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

// Google OAuth Web client (shared with the family-calendar app). The Flowio
// origin (https://flowio.omahoneyalan1.workers.dev) must be added to this
// client's Authorized JavaScript origins in Google Cloud Console.
export const GOOGLE_CLIENT_ID =
  "427396066653-a25j11988d0s0opsmsehn53u5uhicn62.apps.googleusercontent.com";

export type FlowioUser = { email: string; name: string };
// role is a builtin (owner/admin/closer/setter/fulfillment), a legacy
// "member" (treated as closer server-side), or an agency-defined custom
// role slug from role_def. Never assume the old 3-value enum.
export type Agency = {
  id: string;
  name: string;
  slug: string | null;
  role: string;
  member: string;
};

export type SignupMode = "open" | "invite_only" | "allowlist";

type MeResponse = {
  user: FlowioUser | null;
  agencies?: Agency[];
  activeAgency?: Agency | null;
  signupMode?: SignupMode;
  canCreateAgency?: boolean;
};

type AuthCtx = {
  user: FlowioUser | null | undefined;
  agencies: Agency[];
  activeAgency: Agency | null;
  signupMode: SignupMode;
  canCreateAgency: boolean;
  handleCredential: (credential: string) => Promise<void>;
  devSignIn: (email?: string) => Promise<void>;
  signOut: () => Promise<void>;
  createAgency: (name: string) => Promise<Agency>;
  switchAgency: (id: string) => Promise<void>;
  reloadAgencies: () => Promise<void>;
  error: string | null;
};

const Ctx = createContext<AuthCtx | null>(null);

let gisPromise: Promise<any> | null = null;
export function loadGis(): Promise<any> {
  if (gisPromise) return gisPromise;
  gisPromise = new Promise((resolve, reject) => {
    const w = window as any;
    if (w.google?.accounts?.id) return resolve(w.google);
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve((window as any).google);
    s.onerror = () => reject(new Error("Failed to load Google Sign-In"));
    document.head.appendChild(s);
  });
  return gisPromise;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [user, setUser] = useState<FlowioUser | null | undefined>(undefined);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [activeAgency, setActiveAgency] = useState<Agency | null>(null);
  const [signupMode, setSignupMode] = useState<SignupMode>("invite_only");
  const [canCreateAgency, setCanCreateAgency] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function absorbMe(r: MeResponse) {
    setUser(r.user ?? null);
    setAgencies(r.agencies ?? []);
    setActiveAgency(r.activeAgency ?? null);
    if (r.signupMode) setSignupMode(r.signupMode);
    if (typeof r.canCreateAgency === "boolean") setCanCreateAgency(r.canCreateAgency);
  }

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get<MeResponse>("/me");
        // Accept any invite the URL is carrying, then refresh — the accept
        // endpoint requires an authenticated session, so this only fires when
        // the user is already signed in.
        const url = new URL(window.location.href);
        const token = url.searchParams.get("invite");
        if (me.user && token) {
          try {
            await api.post(`/invites/${encodeURIComponent(token)}/accept`);
            url.searchParams.delete("invite");
            window.history.replaceState({}, "", url.toString());
            const refreshed = await api.get<MeResponse>("/me");
            absorbMe(refreshed);
            return;
          } catch { /* fall through to the original response */ }
        }
        absorbMe(me);
      } catch {
        setUser(null);
      }
    })();
  }, []);

  async function handleCredential(credential: string) {
    setError(null);
    try {
      const r = await api.post<MeResponse & { ok: boolean }>("/auth", { credential });
      absorbMe(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Sign-in failed");
      setUser(null);
    }
  }

  async function devSignIn(email?: string) {
    setError(null);
    try {
      const r = await api.post<MeResponse & { ok: boolean }>("/auth/dev", { email });
      absorbMe(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Local sign-in failed");
      setUser(null);
    }
  }

  async function signOut() {
    try { await api.del("/auth"); } catch { /* ignore */ }
    const g = (window as any).google;
    g?.accounts?.id?.disableAutoSelect?.();
    setUser(null);
    setAgencies([]);
    setActiveAgency(null);
    qc.removeQueries();
  }

  async function reloadAgencies() {
    const r = await api.get<{ agencies: Agency[] }>("/agencies");
    setAgencies(r.agencies);
    // Keep activeAgency in sync if it disappeared (e.g. after being removed)
    if (r.agencies.length && activeAgency && !r.agencies.find((w) => w.id === activeAgency.id)) {
      setActiveAgency(r.agencies[0]);
    }
  }

  async function createAgency(name: string): Promise<Agency> {
    const r = await api.post<{ ok: boolean; agency: Agency }>("/agencies", { name });
    setAgencies((prev) => [...prev, r.agency]);
    setActiveAgency(r.agency);
    // Removing then invalidating forces every mounted query to refetch against
    // the new agency. clear() alone leaves subscribers on stale data.
    qc.resetQueries();
    return r.agency;
  }

  async function switchAgency(id: string) {
    const target = agencies.find((w) => w.id === id);
    if (!target) return;
    await api.post(`/agencies/${id}/select`);
    setActiveAgency(target);
    qc.resetQueries();
  }

  return (
    <Ctx.Provider
      value={{
        user, agencies, activeAgency,
        signupMode, canCreateAgency,
        handleCredential, devSignIn, signOut,
        createAgency, switchAgency, reloadAgencies,
        error,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAuth must be used within AuthProvider");
  return c;
}
